<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>Orders</h2>
      <button class="btn btn-primary" @click="openAddModal">Add Order</button>
    </div>

    <!-- Search -->
    <div class="mb-3">
      <input v-model="search" type="text" class="form-control" placeholder="Search orders..." />
    </div>

    <!-- Orders Table -->
    <table class="table table-hover table-striped">
      <thead class="table-dark">
        <tr>
          <th>Customer</th>
          <th>Product</th>
          <th>Quantity</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="order in filteredOrders" :key="order.id">
          <td>{{ order.customer.name }}</td>
          <td>{{ order.product.name }}</td>
          <td>{{ order.quantity }}</td>
          <td>{{ order.status }}</td>
          <td>
            <button class="btn btn-sm btn-warning me-2" @click="openEditModal(order)">Edit</button>
            <button class="btn btn-sm btn-danger me-2" @click="deleteOrder(order.id)">Delete</button>
            <button class="btn btn-sm btn-info" @click="markShipped(order)">Mark Shipped</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Add/Edit Order Modal -->
    <div class="modal fade" ref="orderModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="saveOrder">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? 'Edit Order' : 'Add Order' }}</h5>
              <button type="button" class="btn-close" @click="hideModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Customer</label>
                <input v-model="form.customer" type="text" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">Product</label>
                <input v-model="form.product" type="text" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">Quantity</label>
                <input v-model.number="form.quantity" type="number" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-select" required>
                  <option value="Pending">Pending</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="hideModal">Cancel</button>
              <button type="submit" class="btn btn-primary">{{ isEditing ? 'Update' : 'Add' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '../api/axios'
import * as bootstrap from 'bootstrap'

const orders = ref([])
const search = ref('')
const form = ref({})
const isEditing = ref(false)

const orderModal = ref(null)
let orderModalInstance = null

const filteredOrders = computed(() => {
  if (!search.value) return orders.value
  return orders.value.filter(o =>
    o.customer.name.toLowerCase().includes(search.value.toLowerCase()) ||
    o.product.name.toLowerCase().includes(search.value.toLowerCase())
  )
})

const fetchOrders = async () => {
  try {
    const res = await api.get('/salesorders/')
    orders.value = res.data
  } catch (err) {
    console.error(err)
  }
}

onMounted(() => {
  fetchOrders()
  orderModalInstance = new bootstrap.Modal(orderModal.value)
})

const openAddModal = () => {
  isEditing.value = false
  form.value = { customer: '', product: '', quantity: 1, status: 'Pending' }
  orderModalInstance.show()
}

const openEditModal = (order) => {
  isEditing.value = true
  form.value = { ...order, customer: order.customer.name, product: order.product.name }
  orderModalInstance.show()
}

const hideModal = () => orderModalInstance.hide()

const saveOrder = async () => {
  try {
    if (isEditing.value) {
      await api.put(`/salesorders/${form.value.id}/`, form.value)
    } else {
      await api.post('/salesorders/', form.value)
    }
    fetchOrders()
    hideModal()
  } catch (err) {
    console.error(err)
  }
}

const deleteOrder = async (id) => {
  if (!confirm('Are you sure?')) return
  try {
    await api.delete(`/salesorders/${id}/`)
    fetchOrders()
  } catch (err) {
    console.error(err)
  }
}

const markShipped = async (order) => {
  try {
    await api.post(`/salesorders/${order.id}/ship/`)
    fetchOrders()
  } catch (err) {
    console.error(err)
  }
}
</script>
